import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import moment from "moment";
import JSZip from "jszip";

const blobSas = import.meta.env.VITE_BLOB_SAS;

export const exportToXLSX = (data, filename) => {
  const selectedFields = [
    "_id",
    "createdAt",
    "status",
    "firstName",
    "middleName",
    "lastName",
    "learningCenters",
    "studentPrimaryPhone",
    "pgPrimaryEmail",
    "fileName",
    "uploadedUrl",
    "PermaLink",
    "_uuid",
    "courses",
    "course1", // Add course1
    "course2", // Add course2
    "course3", // Add course3
    "course4", // Add course4
    "selectedCourseAgreements",
    "charter",
    "charter_id",
    "hasPreviouslyEnrolled",
    "gender",
    "birthDate",
    "studentEmail",
    "studentPrimaryPhoneType",
    "pgRelationshipToStudent",
    "pgFirstName",
    "pgLastName",
    "pgPrimaryPhone",
    "pgPrimaryPhoneType",
    "liveWithPg",
    "livingStatus",
    "street",
    "city",
    "state",
    "zip",
    "country",
    "district",
    "isMailingAddressTheSame",
    "mailingStreet",
    "mailingCity",
    "mailingState",
    "mailingZip",
    "gradeLevel",
    // "studentSignature",
    "studentName",
    "studentSignatureDate",
    // "parentSignature",
    "parentName",
    "parentSignatureDate",
    "LeloRegistrationProfile",
    "createdAt",
    "updatedAt",
    "siteId",
  ];

  const formattedData = [];

  data.forEach((entry) => {
    const formattedEntry = {};

    selectedFields.forEach((field) => {
      if (field === "fileName") {
        // Check if entry has multiple PDFs
        if (entry.uploadedPdfs && Array.isArray(entry.uploadedPdfs) && entry.uploadedPdfs.length > 0) {
          // Join all filenames with comma separator
          formattedEntry[field] = entry.uploadedPdfs.map(pdf => pdf.fileName || "").join(", ");
        } else if (entry.fileName) {
          // Old format: single PDF stored in fileName string field
          formattedEntry[field] = entry.fileName;
        } else {
          formattedEntry[field] = "";
        }
      } else if (field === "uploadedUrl") {
        // Check if entry has multiple PDFs
        if (entry.uploadedPdfs && Array.isArray(entry.uploadedPdfs) && entry.uploadedPdfs.length > 0) {
          // Join all URLs with comma separator
          formattedEntry[field] = entry.uploadedPdfs.map(pdf => pdf.url || "").join(", ");
        } else if (entry.uploadedUrl) {
          // Old format: single PDF stored in uploadedUrl string field
          formattedEntry[field] = entry.uploadedUrl;
        } else {
          formattedEntry[field] = "";
        }
      } else if (entry[field] !== undefined) {
        if (field === "courses" && Array.isArray(entry[field])) {
          // Convert courses array to course1,2,3,4
          formattedEntry["course1"] = entry[field][0]?.course_name?.[0] || "";
          formattedEntry["course2"] = entry[field][1]?.course_name?.[0] || "";
          formattedEntry["course3"] = entry[field][2]?.course_name?.[0] || "";
          formattedEntry["course4"] = entry[field][3]?.course_name?.[0] || "";
        } else if (field === "selectedCourseAgreements" && Array.isArray(entry[field])) {
          // Convert selectedCourseAgreements array to a readable string
          formattedEntry[field] = entry[field].join(", ");
        } else if (field === "createdAt") {
          formattedEntry[field] = moment(entry[field]).format("MM/DD/YYYY"); // Format createdAt
        } else {
          formattedEntry[field] = entry[field];
        }
      }
    });

    formattedData.push(formattedEntry);
  });

  const ws = XLSX.utils.json_to_sheet(formattedData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Lelo-Form");
  XLSX.writeFile(wb, filename);
};

const capitalizeFirstLetter = (string) => {
  return string
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Utility function to format the date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}/${day}/${year}`;
};

export const exportToPDF = (formData, filename) => {
  const doc = new jsPDF();
  let yPosition = 10;

  // Add title
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(
    10,
    yPosition,
    `LELO Registration Form of ${formData.firstName} ${formData.lastName}`
  );
  yPosition += 15;

  // Define section headings based on age
  let sections;

  sections = {
    "Learning Center Information": ["learningCenters", "charter","_uuid"],
    "Student Information": [
      "hasPreviouslyEnrolled",
      "firstName",
      "middleName",
      "lastName",
      "gender",
      "birthDate",
      "studentEmail",
      "studentPrimaryPhone",
      "studentPrimaryPhoneType",
    ],
    "Parent Information": [
      "pgRelationshipToStudent",
      "pgFirstName",
      "pgLastName",
      "pgPrimaryEmail",
      "pgPrimaryPhone",
      "pgPrimaryPhoneType",
    ],

    "Physical Address": [
      "liveWithPg",
      "livingStatus",
      "street",
      "city",
      "state",
      "zip",
      "country",
      "district",
    ],
    "Mailing Address": [
      "mailingStreet",
      "mailingCity",
      "mailingState",
      "mailingZip",
    ],
    "Registration for Classes": [
      "hasPreviouslyEnrolled",
      "gradeLevel",
      "courses",
      "selectedCourseAgreements",
    ],
    "Master Agreement": [
      "studentSignature",
      "studentName",
      "studentSignatureDate",
      "parentSignature",
      "parentName",
      "parentSignatureDate",
    ],
  };

  const mailingFields = [
    "mailingStreet",
    "mailingCity",
    "mailingState",
    "mailingZip",
  ];
  const isMailingAddressEmpty = mailingFields.every(
    (field) => !formData[field] || formData[field].trim() === ""
  );

  if (isMailingAddressEmpty) {
    delete sections["Mailing Address"];
  }

  if (formData.status === "Active") {
    const checkEmpty = (fields) => fields.every((f) => !formData[f] || formData[f].trim?.() === "");

    const optionalSections = {
      "Parent Information": ["pgRelationshipToStudent", "pgFirstName", "pgLastName", "pgPrimaryEmail", "pgPrimaryPhone", "pgPrimaryPhoneType"],
      "Physical Address": ["liveWithPg", "livingStatus", "street", "city", "state", "zip", "country", "district"],
      "Master Agreement": ["studentSignature", "studentName", "studentSignatureDate", "parentSignature", "parentName", "parentSignatureDate"],
    };

    for (const [section, fields] of Object.entries(optionalSections)) {
      if (checkEmpty(fields)) delete sections[section];
    }
  }

  // Add content to the PDF
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  Object.entries(sections).forEach(([sectionTitle, keys]) => {
    const capitalizedSectionTitle = capitalizeFirstLetter(sectionTitle);

    doc.setFontSize(12); // Set font size for section headings
    doc.setFont("helvetica", "bold");
    doc.text(15, yPosition, capitalizedSectionTitle);
    yPosition += 10;

    doc.setFontSize(12); // Set font size for field values
    doc.setFont("helvetica", "normal");

    keys.forEach((key) => {
      if (formData[key]) {
        let value = formData[key];
        let capitalizedField = capitalizeFirstLetter(
          key.replace(/([A-Z])/g, " $1").trim()
        );

        if (key === "_uuid") {
          capitalizedField = "Registration ID";
        }

        capitalizedField = capitalizedField.replace(/\bpg\b/gi, "Parent");

        capitalizedField = capitalizedField.replace(
          /\bLearning Centers\b/g,
          "Learning Center"
        );

        capitalizedField = capitalizedField.replace(/\bCountry\b/g, "County");

        // Replace specific field names with more readable text
        capitalizedField = capitalizedField.replace(
          /\bSame Address\b/g,
          "Mailing Address Same"
        );

        capitalizedField = capitalizedField.replace(
          /\bStudent First Name\b/g,
          "First Name"
        );

        capitalizedField = capitalizedField.replace(
          /\bStudent Last Name\b/g,
          "Last Name"
        );

        capitalizedField = capitalizedField.replace(
          /\bPhone\b/g,
          "Phone Number"
        );
        capitalizedField = capitalizedField.replace(
          /\bLive With Pg\b/g,
          "Lives With Parent"
        );

        if (key === "studentSignature" || key === "parentSignature") {
          // Add image
          const signatureDataURL = formData[key]; 
          if (signatureDataURL) {
            const capitalizedKey = key.replace(/([A-Z])/g, " $1").trim();
            const finalKey =
              capitalizedKey.charAt(0).toUpperCase() + capitalizedKey.slice(1);
            doc.text(15, yPosition, `${finalKey}:`);
            yPosition += 10;

            doc.addImage(
              signatureDataURL,
              "PNG",
              15,
              yPosition,
              50,
              20 // Adjust width/height as needed
            );
            yPosition += 25;

            if (yPosition > 280) {
              doc.addPage();
              yPosition = 15;
            }
          }
        } else if (key === "learningCenters") {
          // Ensure Learning Centers is displayed only once
          const uniqueLearningCenters = Array.isArray(value)
            ? [...new Set(value)]
            : [value];
          uniqueLearningCenters.forEach((center, index) => {
            doc.text(15, yPosition, `${capitalizedField}: ${center}`);
            yPosition += 10;

            if (yPosition > 280) {
              doc.addPage();
              yPosition = 15;
            }
          });
        } else if (key === "courses" && Array.isArray(value)) {
          value.forEach((course, index) => {
            const courseName = course.course_name || `Course ${index + 1}`;
            const agreement =
              formData.selectedCourseAgreements?.[index] || "N/A";

            doc.text(
              15,
              yPosition,
              `Course ${index + 1}: ${courseName} - Credits: ${agreement}`
            );
            yPosition += 10;

            if (yPosition > 280) {
              doc.addPage();
              yPosition = 15;
            }
          });
        } else if (key !== "courses" && key !== "selectedCourseAgreements") {
          if (Array.isArray(value)) {
            // Handle other array fields
            value.forEach((item, index) => {
              doc.text(
                15,
                yPosition,
                `${capitalizeFirstLetter(key)} [${index + 1}]: ${item}`
              );
              yPosition += 10;

              if (yPosition > 280) {
                doc.addPage();
                yPosition = 15;
              }
            });
          } else if (
            key === "birthDate" ||
            key === "studentSignatureDate" ||
            key === "parentSignatureDate"
          ) {
            const formattedDate = formatDate(value);
            value = `${formattedDate}`;
          }

          doc.text(15, yPosition, `${capitalizedField}: ${value}`);
          yPosition += 10;
          if (yPosition > 280) {
            doc.addPage();
            yPosition = 15;
          }
        }
      }
    });

    // Add extra spacing after each section
    yPosition += 8;
    if (yPosition > 280) {
      doc.addPage();
      yPosition = 15;
    }
  });

  // Save the PDF
  doc.save(filename);
};

export const exportToZIP = async (data, filename) => {
  if (!data || data.length === 0) {
    console.error("No data available for export.");
    return;
  }
  const zip = new JSZip();

  // Process each entry and fetch PDFs
  for (const entry of data) {
    // Check if there are multiple PDFs stored in uploadedPdfs array
    if (entry.uploadedPdfs && Array.isArray(entry.uploadedPdfs) && entry.uploadedPdfs.length > 0) {
      // New format: Multiple PDFs - download all of them
      for (const pdf of entry.uploadedPdfs) {
        try {
          const downloadURL = `${pdf.url}?${blobSas}`;
          //console.log(`Downloading PDF for ${entry.firstName} from ${downloadURL}`);
          const response = await fetch(downloadURL);

          if (!response.ok) {
            console.error(`Failed to fetch PDF for ${entry.firstName}: ${response.statusText}`);
            continue; // Skip this PDF if the fetch fails
          }

          const pdfBlob = await response.blob();
          // Use the actual filename stored in the database
          zip.file(pdf.fileName, pdfBlob);

        } catch (error) {
          console.error(`Error downloading PDF: ${error}`);
        }
      }
    } else if (entry.uploadedUrl) {
      // Old format: Single PDF stored in uploadedUrl
      try {
        const downloadURL = `${entry.uploadedUrl}?${blobSas}`;
        //console.log(`Downloading PDF for ${entry.firstName} from ${downloadURL}`);
        const response = await fetch(downloadURL);

        if (!response.ok) {
          console.error(`Failed to fetch PDF for ${entry.firstName}: ${response.statusText}`);
          continue; // Skip this entry if the fetch fails
        }

        const pdfBlob = await response.blob();

        // Extract filename from URL
        let filename = `${entry.firstName|| "Unknown"} ${entry.lastName} ${entry._uuid}.pdf`;
        if (entry.fileName) {
          // If fileName exists in database, use it
          filename = entry.fileName;
        } else {
          // Otherwise, try to extract from URL
          const urlParts = entry.uploadedUrl.split('/');
          const filenameFromUrl = urlParts[urlParts.length - 1];
          if (filenameFromUrl && filenameFromUrl.endsWith('.pdf')) {
            filename = decodeURIComponent(filenameFromUrl);
          }
        }

        zip.file(filename, pdfBlob);

      } catch (error) {
        console.error(`Error downloading PDF: ${error}`);
      }
    }
  }

  // Generate ZIP and trigger download
  zip.generateAsync({ type: "blob" }).then((blob) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};
